import {BuildGameConfigurations, ModeSettingEnum} from '@colonist/game-base-types'
import {UIAdController} from '../../../shared/scripts/ui_ad/UIAdController'
import {UIWGameCanvasController} from './UIWGameCanvasController'
import {UIGameManager} from '../../../shared/scripts/ui_game/UIGameManager'
import {UIGameActionsController} from '../../../shared/scripts/ui_game/ui_game_actions/UIGameActionsController'
import {UIGameCardController} from '../../../shared/scripts/ui_game/ui_game_card/UIGameCardController'
import {UIWGameMapController} from './ui_game_map/UIWGameMapController'
import {UIWGameToolTipController} from './ui_game_tooltip/UIWGameToolTipController'
import {UIGameTurnSoundController} from '../../../shared/scripts/ui_game/UIGameTurnSoundController'
import {UIGamePlayerInformationLongestRoadController} from '../../../shared/scripts/ui_game/ui_game_players/UIGamePlayerInformationLongestRoadController'
import {UIGamePlayerInformationLargestArmyController} from '../../../shared/scripts/ui_game/ui_game_players/UIGamePlayerInformationLargestArmyController'
import {UIGameBarbarianInvasion} from '../../../shared/scripts/ui_game/ui_game_cities_and_knights/UIGameBarbarianInvasion'
import {UIGameCityImprovement} from '../../../shared/scripts/ui_game/ui_game_cities_and_knights/UIGameCityImprovement'
import {UIGameKnightController} from '../../../shared/scripts/ui_game/ui_game_cities_and_knights/UIGameKnightController'
import {UIGameMapCityWallController} from '../../../shared/scripts/ui_game/ui_game_map/UIGameMapCityWallController'
import {UIGameFogTileController} from '../../../shared/scripts/ui_game/ui_game_map/UIGameFogTileController'
import {UIGameGoldTileController} from '../../../shared/scripts/ui_game/ui_game_map/UIGameGoldTileController'
import {UIGameMapShipController} from '../../../shared/scripts/ui_game/ui_game_map/UIGameMapShipController'
import {UIGameMapPirateController} from '../../../shared/scripts/ui_game/ui_game_map/UIGameMapPirateController'
import {UIGameMapChitController} from '../../../shared/scripts/ui_game/ui_game_map/UIGameMapChitController'
import {UIWGameLayout} from './UIWGameLayout'
import {UIGameController} from '../../../shared/scripts/ui_game/UIGameController'
import {UIGameMerchantController} from '../../../shared/scripts/ui_game/ui_game_map/UIGameMapMerchantController'
import {GameState, NestedReadonly} from '@colonist/game-states'
import {UIGameMapRobberController} from '../../../shared/scripts/ui_game/ui_game_map/UIGameMapRobberController'
import {UIWGameTabVisibilityController} from '../../../../client/web/scripts/ui_game/UIWGameTabVisibilityController'
import {UIGamePauseController} from '../../../shared/scripts/ui_game/UIGamePauseController'
import {UIGameAchievementController} from '../../../shared/scripts/ui_game/ui_game_achievement/UIGameAchievementController'
import {UIGamePopupController} from '../../../shared/scripts/ui_game/ui_game_popup/UIGamePopupController'
import {UIGameInventorHighlightDiceController} from '../../../shared/scripts/ui_game/ui_game_map/UIGameInventorHighlightDiceController'

export class UIWGameManager extends UIGameManager {
    constructor(adController?: UIAdController) {
        super(adController)

        this.canvasController = new UIWGameCanvasController(this)
        this.toolTipController = new UIWGameToolTipController(this)
    }

    buildGame(configurations: BuildGameConfigurations) {
        this.gameController = new UIGameController(this, configurations)

        super.buildGame(configurations)

        new UIWGameTabVisibilityController(this)

        const {
            mechanicSettlementState,
            mechanicCityState,
            mechanicRoadState,
            mechanicRobberState,
        } = configurations.gameState

        this.gameLayout = new UIWGameLayout(this)
        this.mapController = new UIWGameMapController(this) // Jan 28, 2021. Jeff: Map first so its below containers
        this.actionsController = new UIGameActionsController(this, mechanicSettlementState, mechanicCityState, mechanicRoadState)
        this.cardController = new UIGameCardController(this, mechanicRobberState)

        new UIGameAchievementController(this)
        new UIGameTurnSoundController(this)
        new UIGamePopupController(this)
        this.pauseController = new UIGamePauseController(this)

        new UIGameGoldTileController(this)
        this.buildOrderDependentControllers(configurations.gameState)

        const isColonistRush = this.gameController.gameSettings.modeSetting == ModeSettingEnum.ColonistRush4P
        if(isColonistRush) this.addColonistRushBranding()
    }

    private buildOrderDependentControllers(gameState: NestedReadonly<GameState>) {
        this.mapController.createMap()
        this.buildOptionalMechanics(gameState)
        this.mapController.mapView.sortChildren()
        if(this.originalMapPosition != undefined) {
            this.mapController.mapView.scale.set(this.originalMapPosition.zoom.x, this.originalMapPosition.zoom.y)
            this.mapController.mapView.emit('zoomed', this.mapController.mapView.DEFAULT_ZOOM_EVENT)
            this.mapController.mapView.repositionMap(this.originalMapPosition.location)
        }
    }

    protected buildOptionalMechanics(gameState: NestedReadonly<GameState>) {
        if(gameState.mechanicCityWallState) {
            new UIGameMapCityWallController(this, gameState.mechanicCityWallState)
        }

        if(gameState.mechanicShipState) {
            new UIGameMapShipController(this, gameState.mechanicShipState, gameState.mechanicPirateState)
        }

        if(gameState.mechanicLongestRoadState) {
            new UIGamePlayerInformationLongestRoadController(this, gameState.mechanicLongestRoadState)
        }

        if(gameState.mechanicLargestArmyState) {
            new UIGamePlayerInformationLargestArmyController(this, gameState.mechanicLargestArmyState, gameState.mechanicDevelopmentCardsState)
        }

        if(gameState.mechanicRobberState) {
            new UIGameMapRobberController(this, gameState.mechanicRobberState)
        }

        if(gameState.mechanicPirateState) {
            new UIGameMapPirateController(this, gameState.mechanicPirateState)
        }

        if(gameState.mechanicIslandExplorationState) {
            new UIGameMapChitController(this)
        }

        if(gameState.mapState.fogTileGroupStates) {
            new UIGameFogTileController(this, gameState.mapState.fogTileGroupStates)
        }

        if(gameState.mechanicKnightState) {
            new UIGameKnightController(this, gameState.mechanicKnightState, gameState.mechanicRobberState, gameState.mechanicPirateState)
        }

        if(gameState.mechanicMerchantState) {
            new UIGameMerchantController(this, gameState.mechanicMerchantState)
        }

        if(gameState.mechanicBarbarianInvasionState && gameState.mechanicKnightState) {
            new UIGameBarbarianInvasion(this, gameState.mechanicBarbarianInvasionState, gameState.mechanicKnightState)
        }

        if(gameState.mechanicProgressCardsState) {
            new UIGameInventorHighlightDiceController(this)
        }

        if(gameState.mechanicCityImprovementsState) {
            new UIGameCityImprovement(this, gameState.mechanicCityImprovementsState, gameState.mechanicRobberState)
        }

        super.buildOptionalMechanics(gameState)
    }

    // eslint-disable-next-line class-methods-use-this
    openShareLinkMobileModal(link: string) {
        throw new Error('Link sharing not supported on web')
    }
}
